/**
 * Limpeza de anúncios duplicados de uma conta: mostra cada grupo (mesmo SKU e mesmo tipo), o que fica e o que sai,
 * e exclui os que sobram depois da confirmação digitada.
 */
import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2, X } from 'lucide-react';
import { api, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from './ui.tsx';

interface DupListing { externalId: string; title: string | null; status: string; listingType: string | null; catalog: boolean; buyBox: string | null; stock: number; sold90: number }
interface DupGroup { sku: string; productTitle: string | null; listingType: string | null; keep: DupListing; remove: DupListing[] }

const TYPE: Record<string, string> = { gold_special: 'Clássico', gold_pro: 'Premium', free: 'Grátis' };
const STATUS: Record<string, string> = { active: 'ativo', paused: 'pausado' };

function Line({ l, keep }: { l: DupListing; keep?: boolean }) {
  return (
    <div className="small" style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', textDecoration: keep ? undefined : 'line-through', opacity: keep ? 1 : 0.75 }}>
      <span className="code">{l.externalId}</span>
      <Tag tone={l.status === 'active' ? 'ok' : 'off'}>{STATUS[l.status] ?? l.status}</Tag>
      {l.catalog && <Tag tone="info">catálogo</Tag>}
      {l.buyBox === 'winning' && <Tag tone="ok">ganhando</Tag>}
      <span className="muted">{int(l.sold90)} vendidos em 90 dias · estoque {int(l.stock)}</span>
    </div>
  );
}

export function DuplicatesButton({ accountId, catalogApart }: { accountId: string; catalogApart?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="btn danger" type="button" onClick={() => setOpen(true)}><Trash2 size={16} /> Excluir duplicados</button>
      {open && <DuplicatesDialog accountId={accountId} catalogApart={!!catalogApart} onClose={() => setOpen(false)} />}
    </>
  );
}

function DuplicatesDialog({ accountId, catalogApart, onClose }: { accountId: string; catalogApart: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [apart, setApart] = useState(catalogApart);
  const [typed, setTyped] = useState('');
  const q = useQuery({
    queryKey: ['duplicates', accountId, apart],
    queryFn: () => api<{ channel: string; groups: DupGroup[]; remove: number }>(`/channels/accounts/${accountId}/duplicates?catalogApart=${apart}`),
  });
  const clean = useMutation({
    mutationFn: () => api<{ queued: number }>(`/channels/accounts/${accountId}/duplicates/clean`, {
      method: 'POST', json: { catalogApart: apart, confirm: typed, externalIds: q.data?.groups.flatMap((g) => g.remove.map((r) => r.externalId)) },
    }),
    onSuccess: () => qc.invalidateQueries(),
  });
  const d = q.data;
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="drawer-overlay" />
        <Dialog.Content className="panel" aria-describedby={undefined}
          style={{ position: 'fixed', top: '5vh', left: '50%', transform: 'translateX(-50%)', width: 'min(760px, calc(100vw - 32px))', maxHeight: '90vh', overflow: 'auto', zIndex: 60, display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Dialog.Title asChild><h2 style={{ margin: 0 }}>Excluir anúncios duplicados</h2></Dialog.Title>
            <span style={{ flex: 1 }} />
            <Dialog.Close className="icon-btn" aria-label="Fechar"><X size={18} /></Dialog.Close>
          </div>
          <p className="small muted" style={{ margin: 0 }}>
            Duplicado é mais de um anúncio ativo ou pausado do mesmo SKU e do mesmo tipo (Clássico e Premium do mesmo SKU não contam).
            Em cada grupo fica o melhor — ativo antes de pausado, ganhando o buy box, mais vendido nos últimos 90 dias, com estoque — e os outros são finalizados e excluídos no canal.
          </p>
          {d?.channel === 'mercadolivre' && (
            <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" className="check" checked={apart} onChange={(e) => setApart(e.target.checked)} />
              Manter um de catálogo e um tradicional do mesmo SKU (não contam como duplicados entre si)
            </label>
          )}
          {q.isPending ? <Loading /> : q.error ? <ErrorBox error={q.error} /> : d && (d.groups.length === 0 ? (
            <div className="empty"><b>Nenhum duplicado</b>Cada SKU tem no máximo um anúncio de cada tipo nesta conta.</div>
          ) : (
            <>
              <div><b>{int(d.groups.length)}</b> SKU(s) com duplicados · <b>{int(d.remove)}</b> anúncio(s) seriam excluídos.</div>
              <div style={{ display: 'grid', gap: 10, maxHeight: '45vh', overflow: 'auto', border: '1px solid var(--line)', borderRadius: 'var(--radius)', padding: 10 }}>
                {d.groups.slice(0, 300).map((g) => (
                  <div key={`${g.sku}|${g.listingType}|${g.keep.externalId}`} style={{ display: 'grid', gap: 3 }}>
                    <div><b>SKU {g.sku}</b> <span className="small muted">· {TYPE[g.listingType ?? ''] ?? g.listingType ?? 'anúncio'} · {g.productTitle ?? g.keep.title}</span></div>
                    <div className="small" style={{ color: 'var(--ok)' }}>fica:</div>
                    <Line l={g.keep} keep />
                    <div className="small" style={{ color: 'var(--err)' }}>sai:</div>
                    {g.remove.map((r) => <Line key={r.externalId} l={r} />)}
                  </div>
                ))}
                {d.groups.length > 300 && <div className="small muted">e mais {int(d.groups.length - 300)} SKU(s)…</div>}
              </div>
              {clean.isSuccess ? (
                <div className="notice small">{int(clean.data.queued)} anúncio(s) na fila de exclusão. Acompanhe em Fila de envios; a lista atualiza conforme o canal confirma.</div>
              ) : (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span className="small">Exclusão no canal não tem volta. Digite <b>EXCLUIR</b> para confirmar:</span>
                  <input className="input" value={typed} onChange={(e) => setTyped(e.target.value)} style={{ width: 130 }} />
                  <button className="btn danger" type="button" disabled={typed !== 'EXCLUIR' || clean.isPending} onClick={() => clean.mutate()}>
                    {clean.isPending ? 'Enviando…' : `Excluir ${int(d.remove)} anúncio(s)`}
                  </button>
                </div>
              )}
              {clean.error && <ErrorBox error={clean.error} />}
            </>
          ))}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
